import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Alert, Anchor, Box, Button, Code, Container, Group, Loader, SimpleGrid, Stack, Text, TextInput, Title } from "@mantine/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { Logo } from "../components/AppLayout";
import { Turnstile, type TurnstileHandle } from "../components/Turnstile";
import { api, ApiError } from "../lib/http";
import { AccountButton } from "../components/AccountCta";
import type { Me } from "../lib/types";
import { brand } from "../theme/brand";
import { Eyebrow } from "../theme/components";

interface SignupOptions {
  enabled: boolean;
  captcha: { provider: "turnstile"; siteKey: string } | null;
  accessDays: number;
  tokensPerDay: number;
}

interface VerifyResult {
  me: Me;
  created: boolean;
  username?: string;
  password?: string;
  email?: { sent: boolean; mode: "sendgrid" | "log" } | null;
}

function Shell({ aside, children }: { aside?: ReactNode; children: ReactNode }) {
  return (
    <Box className="pd-dark" mih="100vh">
      <Container size="lg" py={28}>
        <Logo />
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing={64} mt={{ base: 40, md: 90 }}>
          <Stack gap="lg">
            <Eyebrow>Passio Nutrition API · free demo</Eyebrow>
            <Title order={1} fz={{ base: 40, sm: 54 }} lh={1.05} c="white">
              Try the Passio Nutrition API
            </Title>
            {aside}
          </Stack>
          <Box p={{ base: 24, sm: 36 }} maw={440} w="100%" style={{ background: "#fff", borderRadius: 18, color: "var(--pd-ink)", justifySelf: "end" }}>
            {children}
          </Box>
        </SimpleGrid>
      </Container>
    </Box>
  );
}

const fmtTokens = (n: number) => (n > 0 ? `${n.toLocaleString("en-US")} tokens a day` : "no daily token cap");

export function SignupPage() {
  const options = useQuery({ queryKey: ["signup-options"], queryFn: () => api<SignupOptions>("GET", "/api/signup/options"), staleTime: 60_000 });
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const captcha = useRef<TurnstileHandle>(null);
  const o = options.data;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api("POST", "/api/signup/start", { name, email, company: company || undefined, captchaToken: captchaToken ?? undefined });
      setSentTo(email.trim());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't start the sign-up. Please try again.");
      captcha.current?.reset();
    } finally {
      setBusy(false);
    }
  };

  const aside = (
    <>
      <Text fz="lg" className="pd-dim" maw={520}>
        {brand.intro}
      </Text>
      {o?.enabled && (
        <Text className="pd-dim" maw={520}>
          A free demo account gives you {o.accessDays} days and {fmtTokens(o.tokensPerDay)} across search, barcodes, meal photos and the AI tools, with the live request and response for every call.
        </Text>
      )}
      <Stack gap={6} align="flex-start">
        <Text fz="sm" className="pd-dim">
          Already know you want Passio?
        </Text>
        <AccountButton placement="signup" size="md" />
      </Stack>
    </>
  );

  if (options.isLoading) {
    return (
      <Shell aside={aside}>
        <Group justify="center" py="xl">
          <Loader />
        </Group>
      </Shell>
    );
  }

  if (!o?.enabled) {
    return (
      <Shell aside={aside}>
        <Stack gap="md">
          <Title order={2} fz={26}>
            Request demo access
          </Title>
          <Text c="dimmed">Self-service sign-up isn't open right now. Email {brand.supportEmail} and we'll set you up.</Text>
          <Anchor component={Link} to="/">
            Already have an account? Sign in
          </Anchor>
        </Stack>
      </Shell>
    );
  }

  if (sentTo) {
    return (
      <Shell aside={aside}>
        <Stack gap="md">
          <Title order={2} fz={26}>
            Check your inbox
          </Title>
          <Text>
            We sent a confirmation link to <b>{sentTo}</b>. Open it within 24 hours to start your demo; it signs you straight in.
          </Text>
          <Text fz="sm" c="dimmed">
            Nothing after a few minutes? Check spam, or{" "}
            <Anchor component="button" type="button" fz="sm" onClick={() => setSentTo(null)}>
              try again
            </Anchor>
            .
          </Text>
        </Stack>
      </Shell>
    );
  }

  return (
    <Shell aside={aside}>
      <form onSubmit={submit}>
        <Stack gap="md">
          <Title order={2} fz={26}>
            Get a free demo account
          </Title>
          <Text c="dimmed" fz="sm">
            We'll email you a link to confirm your address, then you're in.
          </Text>
          {error && <Alert color="red">{error}</Alert>}
          <TextInput label="Name" value={name} onChange={(e) => setName(e.currentTarget.value)} autoComplete="name" maxLength={120} required autoFocus />
          <TextInput label="Work email" type="email" value={email} onChange={(e) => setEmail(e.currentTarget.value)} autoComplete="email" maxLength={254} required />
          <TextInput label="Company" description="Optional" value={company} onChange={(e) => setCompany(e.currentTarget.value)} autoComplete="organization" maxLength={120} />
          {o.captcha && <Turnstile ref={captcha} siteKey={o.captcha.siteKey} onToken={setCaptchaToken} onError={setError} />}
          <Button type="submit" size="md" loading={busy} disabled={Boolean(o.captcha) && !captchaToken} fullWidth>
            Email me a sign-in link
          </Button>
          <Text fz="xs" c="dimmed">
            Already have an account?{" "}
            <Anchor component={Link} to="/" fz="xs">
              Sign in
            </Anchor>
          </Text>
        </Stack>
      </form>
    </Shell>
  );
}

/** Each link works once, so StrictMode's second effect (and remounts) must reuse the first request. */
const verifications = new Map<string, Promise<VerifyResult>>();
const verifyOnce = (token: string) => {
  let p = verifications.get(token);
  if (!p) {
    p = api<VerifyResult>("POST", "/api/signup/verify", { token });
    verifications.set(token, p);
  }
  return p;
};

/** Reads `#token=` once and removes it from the address bar and history entry. */
function takeToken(): string | null {
  const params = new URLSearchParams(window.location.hash.slice(1));
  const token = params.get("token");
  if (token) window.history.replaceState(window.history.state, "", window.location.pathname + window.location.search);
  return token;
}

export function SignupVerifyPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [token] = useState(takeToken);
  const q = useQuery<VerifyResult, ApiError>({
    queryKey: ["signup-verify", token],
    queryFn: () => verifyOnce(token!),
    enabled: Boolean(token),
    retry: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const enter = useCallback(
    (me: Me) => {
      qc.setQueryData(["me"], me);
      navigate("/", { replace: true });
    },
    [qc, navigate],
  );

  // Sign-in links from the provisioning API go straight into the app.
  useEffect(() => {
    if (q.data && !q.data.created) enter(q.data.me);
  }, [q.data, enter]);
  if (q.data && !q.data.created) return null;

  const r = q.data;
  return (
    <Shell>
      <Stack gap="md">
        {!token ? (
          <>
            <Title order={2} fz={26}>
              Link incomplete
            </Title>
            <Text c="dimmed">This page needs the full link from your email. Copy the whole link into the address bar, or sign up again.</Text>
            <Anchor component={Link} to="/signup">
              Sign up
            </Anchor>
          </>
        ) : q.isLoading ? (
          <Group gap="sm">
            <Loader size="sm" />
            <Text>Setting up your demo…</Text>
          </Group>
        ) : q.error ? (
          <>
            <Title order={2} fz={26}>
              {q.error.code === "account_exists" ? "You already have an account" : "That link didn't work"}
            </Title>
            <Alert color={q.error.code === "account_exists" ? "orange" : "red"}>{q.error.message}</Alert>
            <Group gap="lg">
              <Anchor component={Link} to="/">
                Sign in
              </Anchor>
              {q.error.code !== "account_exists" && (
                <Anchor component={Link} to="/signup">
                  Sign up again
                </Anchor>
              )}
            </Group>
          </>
        ) : r ? (
          <>
            <Title order={2} fz={26}>
              You're in
            </Title>
            <Text>Your demo account is ready. Save these details to sign in again later{r.email?.sent ? "; we've emailed them to you too" : ""}.</Text>
            <Stack gap={6}>
              <Text fz="sm" c="dimmed">
                Username
              </Text>
              <Code fz="md">{r.username}</Code>
              <Text fz="sm" c="dimmed">
                Password
              </Text>
              <Code fz="md">{r.password}</Code>
            </Stack>
            <Text fz="sm" c="dimmed">
              You have {fmtTokens(r.me.limits.tokensPerDay)}
              {r.me.user.accessExpiresAt ? ` until ${new Date(r.me.user.accessExpiresAt).toLocaleDateString(undefined, { dateStyle: "long" })}` : ""}.
            </Text>
            <Button size="md" onClick={() => enter(r.me)} fullWidth>
              Open the demo
            </Button>
          </>
        ) : null}
      </Stack>
    </Shell>
  );
}
